import { Loader2, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { SheetClose } from "@/components/ui/sheet";
import { useGetInfiniteNotificationsBo, NotificationsCountResult } from "@/api/notifications.api";
import {
  IN_GLOBAL_NOTIFICATIONS_LOG_KEYS,
  URGENT_LOG_KEYS,
} from "@/constants/log-templates.constants";
import { useMemo, useRef, useState } from "react";
import InfiniteScroll from "../ui/customs/infinite-scroll";
import { NavigateFunction, useNavigate } from "react-router-dom";
import clsx from "clsx";
import {
  NotificationItem,
  NotificationItemNotification,
} from "@/components/shared/notificationItem";
import { NotificationDetailModal } from "@/components/shared/modal/notification.modal";

type FilterTab = "unread" | "urgent" | "all";

const FILTER_TABS: FilterTab[] = ["unread", "urgent", "all"];

export const DEFAULT_NOTIFICATION_KEYS = IN_GLOBAL_NOTIFICATIONS_LOG_KEYS.join(",");
export const URGENT_KEYS_PARAM = URGENT_LOG_KEYS.join(",");

const TAB_PARAMS: Record<FilterTab, { logKeys: string; includeRead?: boolean }> = {
  unread: { logKeys: DEFAULT_NOTIFICATION_KEYS },
  urgent: { logKeys: URGENT_KEYS_PARAM },
  all: { logKeys: DEFAULT_NOTIFICATION_KEYS, includeRead: true },
};

export const NotificationSheetContent = ({
  notificationsCount,
}: {
  notificationsCount: NotificationsCountResult;
}) => {
  const [activeFilter, setActiveFilter] = useState<FilterTab>("unread");
  const [detailNotifId, setDetailNotifId] = useState<string | null>(null);
  const lastDetailNotifRef = useRef<NotificationItemNotification | null>(null);
  const navigate = useNavigate();
  const { t } = useTranslation("boNavigation");

  const filterLabels: Record<FilterTab, string> = {
    unread: t("notificationsSheet.filters.unread"),
    urgent: t("notificationsSheet.filters.urgent"),
    all: t("notificationsSheet.filters.all"),
  };

  const {
    data,
    isLoading: isInitialLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetInfiniteNotificationsBo(TAB_PARAMS[activeFilter]);

  const notifications = useMemo(() => data?.pages.flatMap((p) => p.notifications) ?? [], [data]);

  const detailNotif = useMemo(() => {
    if (!detailNotifId) return null;
    const found = notifications.find((n) => n.id === detailNotifId);
    if (found) lastDetailNotifRef.current = found;
    return lastDetailNotifRef.current;
  }, [notifications, detailNotifId]);

  const showInfiniteScroll = hasNextPage ?? false;

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold">{t("notificationsSheet.title")}</span>
          {notificationsCount && notificationsCount.urgent > 0 && (
            <Badge className="rounded-full bg-red-500 px-2 text-white hover:bg-red-500">
              {notificationsCount.urgent > 99 ? "99+" : notificationsCount.urgent}
            </Badge>
          )}
          {notificationsCount && notificationsCount.normal > 0 && (
            <Badge className="rounded-full bg-blue-500 px-2 text-white hover:bg-blue-500">
              {notificationsCount.normal > 99 ? "99+" : notificationsCount.normal}
            </Badge>
          )}
        </div>
        <SheetClose className="flex h-7 w-7 items-center justify-center rounded-sm text-gray-500 hover:bg-gray-100">
          <X className="h-4 w-4" />
        </SheetClose>
      </div>

      <div className="flex gap-2 border-b px-4 py-3">
        {FILTER_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveFilter(tab)}
            className={clsx(
              "rounded-full border px-3 py-1 text-sm transition-colors",
              activeFilter === tab
                ? "border-gray-800 font-medium text-gray-900"
                : "border-gray-200 text-gray-500 hover:border-gray-400 hover:text-gray-700",
            )}
          >
            {filterLabels[tab]}
          </button>
        ))}
      </div>

      <NotificationList
        notifications={notifications}
        isInitialLoading={isInitialLoading}
        hasMore={hasNextPage}
        loading={isFetchingNextPage}
        next={fetchNextPage}
        showInfiniteScroll={showInfiniteScroll}
        navigate={navigate}
        showActions
        onDetail={(n) => setDetailNotifId(n.id)}
        closeOnClick
      />

      <NotificationDetailModal
        notification={detailNotif}
        open={!!detailNotif}
        onClose={() => setDetailNotifId(null)}
        hideActions
      />
    </div>
  );
};

export type NotificationListProps = {
  notifications: NotificationItemNotification[];
  isInitialLoading: boolean;
  hasMore: boolean;
  loading: boolean;
  next: () => void;
  showInfiniteScroll: boolean;
  navigate?: NavigateFunction;
  onSelect?: (id: string) => void;
  selectedId?: string | null;
  showActions?: boolean;
  onDetail?: (notification: NotificationItemNotification) => void;
  closeOnClick?: boolean;
};

export const NotificationList = ({
  notifications,
  isInitialLoading,
  hasMore,
  loading,
  next,
  showInfiniteScroll,
  navigate,
  onSelect,
  selectedId,
  showActions,
  onDetail,
  closeOnClick,
}: NotificationListProps) => {
  const { t } = useTranslation("boNavigation");

  return (
    <div className="flex-1 overflow-y-auto">
      {notifications.length === 0 && isInitialLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex h-32 items-center justify-center text-sm text-gray-400">
          {t("notificationsSheet.empty")}
        </div>
      ) : (
        <>
          <div>
            {notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                notification={notification}
                navigate={navigate}
                onSelect={onSelect ? () => onSelect(notification.id) : undefined}
                isSelected={selectedId === notification.id}
                showActions={showActions}
                onDetail={onDetail ? () => onDetail(notification) : undefined}
                closeOnClick={closeOnClick}
              />
            ))}
          </div>
          {showInfiniteScroll && (
            <InfiniteScroll hasMore={hasMore} isLoading={loading} next={next} threshold={1}>
              {hasMore && <Loader2 className="mx-auto my-4 h-8 w-8 animate-spin" />}
            </InfiniteScroll>
          )}
        </>
      )}
    </div>
  );
};
